import type { ActionHandler } from './types';

/**
 * Fire-and-forget outbound webhook with bounded retries. Honors the shared
 * abort signal (unmount / step change) and a per-attempt timeout.
 *
 * TF-AUD-013: sends only stepId + actionId + explicit payload — never the raw
 * internal action config.
 */
export const connectToSystemHandler: ActionHandler = {
  type: 'connect_to_system',
  execute(ctx) {
    const endpoint = ctx.config.endpoint;
    if (!endpoint) return;

    const method = ctx.config.method ?? 'POST';
    const maxRetries = Number(ctx.config.retry_count ?? 2);
    const timeoutMs = Number(ctx.config.timeout_ms ?? 10000);
    const authHeader = ctx.config.auth_header;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (authHeader) headers['Authorization'] = String(authHeader);

    const attemptFetch = async (attempt: number): Promise<void> => {
      if (ctx.signal.aborted) return;
      try {
        const timeoutController = new AbortController();
        const timer = setTimeout(() => timeoutController.abort(), timeoutMs);
        ctx.signal.addEventListener('abort', () => timeoutController.abort(), { once: true });
        await fetch(String(endpoint), {
          method,
          headers,
          body:
            method !== 'GET'
              ? JSON.stringify({ stepId: ctx.stepId, actionId: ctx.action.id, payload: ctx.config.payload ?? {} })
              : undefined,
          signal: timeoutController.signal,
        });
        clearTimeout(timer);
      } catch (err) {
        if (ctx.signal.aborted) return;
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
          await new Promise<void>((r) => {
            const t = setTimeout(r, delay);
            ctx.signal.addEventListener('abort', () => { clearTimeout(t); r(); }, { once: true });
          });
          return attemptFetch(attempt + 1);
        }
        ctx.logger.error('[connect_to_system] failed after retries', err instanceof Error ? err : undefined, {
          attempts: maxRetries + 1,
          actionId: ctx.action.id,
          stepId: ctx.stepId,
        });
      }
    };

    void attemptFetch(0);
  },
};
